/*
 * We will use a set of methods to "extract" all the data from a query.
 * For performance reasons we send all the data de-duplicated, but for easier accessing (and legacy code)
 * we want all entities to be linked again. E.g. Captable->Participation->Event.
 * Join by Event ID with full entity.
 */

import { getTreeNodesAsArray } from 'pages/Modelling/ModellingEditPage/SharedComponents/Tree/TreeMap/util'
import { arrToObj, groupBy, onlyUniqueId, unfreeze } from 'utils'
import { date, roundShares, setCurrency } from 'utils/format'
import { filterByShareMode, getAmount } from 'utils/shares'

const extractSafely = (entityMap, entityId, alternativeEntity = undefined) => {
  if (!entityMap[entityId] && !alternativeEntity) {
    const entityType =
      Object.values(entityMap).length > 0 ? Object.values(entityMap)[0].__typename : 'Entity'
    console.log(`NO ${entityType} FOUND`, entityId, entityMap, alternativeEntity)
  }
  return entityMap[entityId] || alternativeEntity
}

const extractCapTable = (c, stakeholderMap, roundMap, shareMap, cnMap, entryMap, eventMap) => {
  c.participations.forEach((p) => {
    extractParticipation(p, stakeholderMap, roundMap, shareMap, cnMap, eventMap, entryMap)
  })
  c.untriggeredEvents = c.untriggeredEvents.map((e) => extractSafely(eventMap, e.id))
  c.termsheet.round = extractSafely(roundMap, c.termsheet.round.id)

  // Fast access to total fully diluted share basis for an investor
  c.totalSharesFDMap = c.participations
    .filter((p) => filterByShareMode(p, { type: 'esop' }))
    .reduce((total, p) => {
      if (!total[p.investor.id]) total[p.investor.id] = 0
      total[p.investor.id] += getAmount(p, { type: 'esop' })
      return total
    }, {})
  c.totalSharesFD = (investorId) => c.totalSharesFDMap[investorId] || 0

  return c
}

// eslint-disable-next-line
const extractParticipation = (p, stakeholderMap, roundMap, shareMap, cnMap, eventMap, entryMap) => {
  p.investor = extractSafely(stakeholderMap, p.investor.id)
  p.round = extractSafely(roundMap, p.round.id)
  if (p.share) p.share = extractSafely(shareMap, p.share.id)
  if (p.convertibleNote) p.convertibleNote = extractSafely(cnMap, p.convertibleNote.id)
  // TODO: Quickfix solution for not loading all entries correctly (+ ParticipationBase.entries => should only be ID not full entry..)
  // if (p.entries) p.entries = p.entries.map((e) => extractSafely(entryMap, e.id))
  p.entries = p.entries.map((e) =>
    extractEntry(e, stakeholderMap, roundMap, shareMap, cnMap, eventMap),
  )
  return p
}

const extractEvent = (e, stakeholderMap, roundMap, shareMap, cnMap, allEvents) => {
  e.round = extractSafely(roundMap, e.round.id)
  if (e.esop) e.esop = extractSafely(stakeholderMap, e.esop.id)
  if (e.backingTable)
    e.backingTable.forEach((b) => {
      b.investor = extractSafely(stakeholderMap, b.investor.id)
    })
  if (e.receiver) e.receiver = extractSafely(stakeholderMap, e.receiver.id)
  if (e.investor) e.investor = extractSafely(stakeholderMap, e.investor.id)
  if (e.buyer) e.buyer = extractSafely(stakeholderMap, e.buyer.id)
  if (e.seller) e.seller = extractSafely(stakeholderMap, e.seller.id)
  if (e.share) e.share = extractSafely(shareMap, e.share.id)
  if (e.buyingShare) e.buyingShare = extractSafely(shareMap, e.buyingShare.id)
  if (e.sellingShare) e.sellingShare = extractSafely(shareMap, e.sellingShare.id)
  if (e.convertibleNote) e.convertibleNote = extractSafely(cnMap, e.convertibleNote.id)
  if (e.type === 'CN_CONVERSION')
    e.cnInvestment = allEvents.find(
      (i) =>
        i.type === 'CN_INVESTMENT' &&
        i.investor.id === e.investor.id &&
        i.convertibleNote.id === e.convertibleNote.id,
    )
  return e
}

const extractEntry = (e, stakeholderMap, roundMap, shareMap, cnMap, eventMap) => {
  e.round = extractSafely(roundMap, e.round.id)
  e.investor = extractSafely(stakeholderMap, e.investor.id)
  if (eventMap[e.event.id]) e.event = extractSafely(eventMap, e.event.id)
  else {
    // Non-persistent Simulation event (e.g. DividendsEvent/ADEvent)
    e.event = extractEvent(
      e.event,
      stakeholderMap,
      roundMap,
      shareMap,
      cnMap,
      Object.values(eventMap),
    )
  }
  if (e.share) e.share = extractSafely(shareMap, e.share.id)
  if (e.convertibleNote) e.convertibleNote = extractSafely(cnMap, e.convertibleNote.id)
  return e
}

const addFieldsToStakeholderMap = (stakeholderMap, project) => {
  const defaultRound = project.rounds.length > 0 ? project.rounds[0] : null
  const eventsByStakeholders = groupBy(
    project.events.filter((e) => !!(e.investor || e.buyer || e.seller || e.receiver || e.esop)),
    (e) => (e.investor || e.buyer || e.seller || e.receiver || e.esop).id,
  )
  Object.values(stakeholderMap).forEach((s) => {
    const stakeholderEvents = eventsByStakeholders.get(s.id)

    if (stakeholderEvents && stakeholderEvents.length > 0) {
      const stakeholderEntryRound = stakeholderEvents
        .map((e) => e.round)
        .sort((r1, r2) => date(r1.date).diff(r2.date))[0]
      s.entryRound = stakeholderEntryRound
    } else {
      s.entryRound = defaultRound
    }
  })

  return stakeholderMap
}

const addAdditionalProjectFields = (project) => {
  // Add entry rounds to stakeholders
  const stakeholderMap = arrToObj(project.stakeholders)
  addFieldsToStakeholderMap(stakeholderMap, project)

  // Update amount of shares field for buy outs
  project.events.forEach((e) => {
    if (e.type === 'BUY_OUT') {
      e.amountOfShares = roundShares(e.amount / e.pricePerShare, {
        roundingMode: project.roundingMode,
      })
    }
  })
}

export const extractProject = (data) => {
  // 2) Return projects data and current project
  let { project } = data
  project = unfreeze(project)

  // Set all implicit included fields
  if (project) {
    const stakeholderMap = arrToObj(project.stakeholders)
    const shareMap = arrToObj(project.shares)
    const roundMap = arrToObj(
      project.capTables.map((c) => c.termsheet.round),
      (r) => r.id,
      (r) => {
        r.share = extractSafely(shareMap, r.share.id)
        return r
      },
    )
    const cnMap = arrToObj(
      project.events.filter((e) => e.type === 'CN_INVESTMENT'),
      (e) => e.convertibleNote.id,
      (e) => {
        if (e.convertibleNote.share)
          e.convertibleNote.share = extractSafely(shareMap, e.convertibleNote.share.id)
        return e.convertibleNote
      },
    )
    const eventMap = arrToObj(
      project.events,
      (e) => e.id,
      (e) => extractEvent(e, stakeholderMap, roundMap, shareMap, cnMap, project.events),
    )
    const entryMap = {} // TODO

    // Fill existing fields (link with full entities)
    project.commonShare = project.shares.find((s) => s.commonShare && !s.commonShareOptions)
    project.commonShareOptions = project.shares.find((s) => s.commonShareOptions)
    project.rounds = project.capTables.map((c) => c.termsheet.round)

    if (project.projectMemberships) {
      project.projectMemberships
        .filter((p) => !!p.defaultInvestor)
        .forEach((p) => {
          p.defaultInvestor = extractSafely(stakeholderMap, p.defaultInvestor.id)
        })
    }
    if (project.projectInvitations) {
      project.projectInvitations
        .filter((p) => !!p.defaultInvestor)
        .forEach((p) => {
          p.defaultInvestor = extractSafely(stakeholderMap, p.defaultInvestor.id)
        })
    }
    if (project.projectDocuments) {
      const userMap = arrToObj(
        project.projectMemberships.map((p) => p.userAccount),
        (u) => u.userId,
      )
      project.projectDocuments.forEach((p) => {
        p.uploader = extractSafely(userMap, p.uploader.userId)
      })
    }
    project.capTables.forEach((c) => {
      extractCapTable(c, stakeholderMap, roundMap, shareMap, cnMap, entryMap, eventMap)
    })

    // Add additional fields
    setCurrency(project.currencyCode)
    if (project.capTables.length > 0) {
      project.capTable = project.capTables[project.capTables.length - 1]
    } else {
      project.capTable = null
    }
    project.investors = project.stakeholders.filter(
      (s) => !['ESOP_POOL', 'EMPLOYEE'].includes(s.type),
    )
    project.esops = project.events.filter((e) => e.type === 'ESOP_CREATION')
    project.esopGrants = project.events.filter((e) => e.type === 'ESOP_GRANT')
    project.investments = project.events.filter((e) =>
      ['EQUITY_INVESTMENT', 'CN_INVESTMENT', 'CN_CONVERSION', 'MANUAL_SHARES'].includes(e.type),
    )
    project.buyOuts = project.events.filter((e) => e.type === 'BUY_OUT')

    addAdditionalProjectFields(project)

    // Update preMoneyValuationAsIfConverted for round-basis instead of today's (except latest)
    project.capTables.forEach((c, idx) => {
      if (idx !== project.capTables.length - 1) {
        c.preMoneyValuationAsIfConverted = c.preMoneyValuationAsIfConvertedRoundBasis
      }
    })
    project.entries = project.capTables
      .flatMap((c) => c.participations.flatMap((p) => p.entries))
      .filter(onlyUniqueId)
  }

  return { project }
}

export const extractExitSummary = (data, project) => {
  const exitSummary = unfreeze(data.exitSummary)

  // NOTE: Important to copy the data to a new object
  // because exit-summaries are not perfectly in sync when captables update
  const stakeholderMap = arrToObj(
    project.__typename === 'ModellingProject' ? project.allStakeholders : project.stakeholders,
    (s) => s.id,
    (s) => ({ ...s }),
  )
  const shareMap = arrToObj(
    project.__typename === 'ModellingProject' ? project.allShares : project.shares,
    (s) => s.id,
    (s) => ({ ...s }),
  )
  const payOutMap = arrToObj(
    project.__typename === 'ModellingProject' ? project.baseProjectPayOuts : project.payOuts,
    (p) => p.id,
    (p) => ({ ...p }),
  )

  if (exitSummary.importantExits) {
    exitSummary.importantExits.forEach((e) => {
      if (e.investorReturns) {
        e.investorReturns.forEach((ir) => {
          ir.investor = extractSafely(stakeholderMap, ir.investor.id)
          if (ir.investorReturnEntries) {
            ir.investorReturnEntries.forEach((r) => {
              r.exitRight = extractSafely(shareMap, r.exitRight.id, r.exitRight)
            })
          }
        })
      }
      if (e.shareReturns) {
        e.shareReturns.forEach((sr) => {
          // If "Debt/CN Payback", then not existent in the shareMap
          sr.share = extractSafely(shareMap, sr.share.id, sr.share)
          if (sr.payOut) sr.payOut = extractSafely(payOutMap, sr.payOut.id)
          if (sr.investors) {
            sr.investors = sr.investors.map((i) => extractSafely(stakeholderMap, i.id))
          }
          if (sr.sharePoint) {
            sr.sharePoint.share = extractSafely(shareMap, sr.sharePoint.share.id)
            if (sr.sharePoint.preferenceAmounts) {
              sr.sharePoint.preferenceAmounts.forEach((pa) => {
                pa.investor = extractSafely(stakeholderMap, pa.investor.id)
              })
            }
          }
        })
      }
    })
  }

  return exitSummary
}

export const extractModellingProject = (data, baseProject) => {
  const { modellingProject } = unfreeze(data)
  const scenarioCapTables = modellingProject.calculationTree
    ? getTreeNodesAsArray(modellingProject.calculationTree.roots).map((s) => s.result)
    : []
  const stakeholderMap = arrToObj([...modellingProject.stakeholders, ...baseProject.stakeholders])
  const shareMap = arrToObj([...modellingProject.shares, ...baseProject.shares])
  const roundMap = arrToObj(
    [...scenarioCapTables.map((c) => c.termsheet.round), ...baseProject.rounds],
    (r) => r.id,
    (r) => {
      r.share = extractSafely(shareMap, r.share.id)
      return r
    },
  )
  const events = [...modellingProject.events, ...baseProject.events]
  const cnMap = arrToObj(
    events.filter((e) => e.type === 'CN_INVESTMENT'),
    (e) => e.convertibleNote.id,
    (e) => {
      if (e.convertibleNote.share)
        e.convertibleNote.share = extractSafely(shareMap, e.convertibleNote.share.id)
      return e.convertibleNote
    },
  )
  const eventMap = arrToObj(
    events,
    (e) => e.id,
    (e) => extractEvent(e, stakeholderMap, roundMap, shareMap, cnMap, events),
  )
  const entryMap = {} // TODO

  modellingProject.rounds = scenarioCapTables.map((c) => c.termsheet.round)
  modellingProject.allStakeholders = Object.values(stakeholderMap)
  modellingProject.allShares = Object.values(shareMap)
  modellingProject.allRounds = Object.values(roundMap)
  modellingProject.allEvents = Object.values(eventMap)
  modellingProject.allEntries = Object.values(entryMap)
  modellingProject.baseProjectPayOuts = baseProject.payOuts
  modellingProject.roundingMode = baseProject.roundingMode
  modellingProject.currencyCode = baseProject.currencyCode
  modellingProject.commonShareOptions = baseProject.commonShare
  modellingProject.commonShare = baseProject.commonShare
  modellingProject.hasPremiumAccess = baseProject.hasPremiumAccess
  modellingProject.company = baseProject.company

  addAdditionalProjectFields(modellingProject)

  scenarioCapTables.forEach((c) => {
    extractCapTable(c, stakeholderMap, roundMap, shareMap, cnMap, entryMap, eventMap)
  })
  modellingProject.entries = scenarioCapTables
    .flatMap((c) => c.participations.flatMap((p) => p.entries))
    .filter(onlyUniqueId)
  modellingProject.allEntries = [...modellingProject.entries, ...baseProject.entries]

  return modellingProject
}

export const extractCalculationNode = (calculationNode, modellingProject, baseProject) => {
  const baseProjectClone = unfreeze(baseProject)
  const stakeholderMap = arrToObj([
    ...modellingProject.stakeholders,
    ...baseProjectClone.stakeholders,
  ])
  const shareMap = arrToObj([...modellingProject.shares, ...baseProjectClone.shares])
  const roundMap = arrToObj(
    [
      ...modellingProject.rounds,
      ...baseProjectClone.rounds,
      calculationNode.result.termsheet.round,
    ],
    (r) => r.id,
    (r) => {
      r.share = extractSafely(shareMap, r.share.id)
      return r
    },
  )
  const events = [...modellingProject.events, ...baseProjectClone.events]
  const cnMap = arrToObj(
    events.filter((e) => e.type === 'CN_INVESTMENT'),
    (e) => e.convertibleNote.id,
    (e) => {
      if (e.convertibleNote.share)
        e.convertibleNote.share = extractSafely(shareMap, e.convertibleNote.share.id)
      return e.convertibleNote
    },
  )
  const eventMap = arrToObj(
    events,
    (e) => e.id,
    (e) => extractEvent(e, stakeholderMap, roundMap, shareMap, cnMap, events),
  )
  const entryMap = {} // TODO

  extractCapTable(
    calculationNode.result,
    stakeholderMap,
    roundMap,
    shareMap,
    cnMap,
    entryMap,
    eventMap,
  )

  return calculationNode
}
